import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { AnimatePresence, motion } from "motion/react";
import type { WallItem } from "./wall.api.js";

type Slide = { key: string; kind: "image" | "video"; src: string; poster?: string; caption?: string };

const IMAGE_MS = 8000;
const VIDEO_CAP_MS = 16000;

function shuffle<T>(a: T[]): T[] {
  const r = [...a];
  for (let i = r.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [r[i], r[j]] = [r[j]!, r[i]!];
  }
  return r;
}

export function Slideshow({
  items,
  fallback,
  coupleName,
}: {
  items: WallItem[];
  fallback: string[];
  coupleName: string;
}) {
  // pool de slides : contributions invités + photos du couple en secours
  const pool = useMemo<Slide[]>(() => {
    const guest: Slide[] = items.map((it) => ({
      key: it.id,
      kind: it.kind,
      src: it.kind === "video" && it.video ? it.video : it.image,
      poster: it.image,
      caption: [it.pseudo, it.note].filter(Boolean).join(" — "),
    }));
    const couple: Slide[] = fallback.map((url, i) => ({
      key: `couple-${i}`,
      kind: "image",
      src: url,
      caption: coupleName,
    }));
    return guest.length ? [...guest, ...couple.slice(0, Math.ceil(guest.length / 6))] : couple;
  }, [items, fallback, coupleName]);

  const [order, setOrder] = useState<Slide[]>(() => shuffle(pool));
  const [idx, setIdx] = useState(0);
  const timer = useRef<number | null>(null);

  // ré-intègre les nouveautés en fin de tour sans casser la lecture courante
  useEffect(() => {
    setOrder((prev) => {
      const known = new Set(prev.map((s) => s.key));
      const fresh = pool.filter((s) => !known.has(s.key));
      return fresh.length ? [...prev, ...shuffle(fresh)] : prev;
    });
  }, [pool]);

  const current = order[idx % Math.max(order.length, 1)];

  const next = () =>
    setIdx((i) => {
      const n = i + 1;
      if (n % order.length === 0) setOrder((o) => shuffle(o));
      return n;
    });

  useEffect(() => {
    if (!current) return;
    if (timer.current) window.clearTimeout(timer.current);
    if (current.kind === "image") {
      timer.current = window.setTimeout(next, IMAGE_MS);
    } else {
      timer.current = window.setTimeout(next, VIDEO_CAP_MS); // filet si la vidéo cale
    }
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, order.length]);

  if (!current) return null;

  const kx = `${(Math.random() * 6 - 3).toFixed(1)}%`;
  const ky = `${(Math.random() * 6 - 3).toFixed(1)}%`;

  return (
    <div className="absolute inset-0 overflow-hidden">
      <AnimatePresence>
        <motion.div
          key={`${current.key}-${idx}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.3, ease: "easeInOut" }}
          className="absolute inset-0"
        >
          {/* Fond flou : remplit l'écran quel que soit le cadrage (portrait inclus). */}
          <div
            className="absolute inset-0 scale-110 bg-cover bg-center blur-2xl brightness-[0.35] saturate-150"
            style={{ backgroundImage: `url("${current.poster ?? current.src}")` }}
          />
          {/* Média affiché en entier, jamais rogné. */}
          {current.kind === "video" ? (
            <video
              src={current.src}
              poster={current.poster}
              autoPlay
              muted
              playsInline
              onEnded={next}
              className="absolute inset-0 h-full w-full object-contain"
            />
          ) : (
            <img
              src={current.src}
              alt=""
              className="absolute inset-0 h-full w-full object-contain"
              style={
                {
                  "--kx": kx,
                  "--ky": ky,
                  animation: `sv-kenburns-soft ${IMAGE_MS + 1500}ms ease-out both`,
                } as CSSProperties
              }
            />
          )}
          {current.caption && (
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="absolute inset-x-0 bottom-[12vh] text-center font-serif text-[2.2vw] text-white [text-shadow:0_2px_20px_rgba(0,0,0,0.7)]"
            >
              {current.caption}
            </motion.p>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
